/**
 * Voice Input — Hybrid Speech-to-Text
 * - Uses browser Web Speech API where supported (Chrome, Edge, Android)
 * - Falls back to MediaRecorder + Whisper upload on iOS Safari
 */

import api from '../services/api';

const SpeechRecognition = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;

export function hasBrowserSTT() {
    return !!SpeechRecognition;
}

export function hasMediaRecorder() {
    return typeof window !== 'undefined'
        && typeof MediaRecorder !== 'undefined'
        && navigator.mediaDevices
        && typeof navigator.mediaDevices.getUserMedia === 'function';
}

export function isVoiceInputSupported() {
    return hasBrowserSTT() || hasMediaRecorder();
}

function detectLang() {
    const browserLang = (navigator.language || 'en-IN').toLowerCase();
    if (browserLang.startsWith('hi')) return 'hi-IN';
    return 'en-IN';
}

/**
 * Browser STT path — live transcription
 */
class BrowserSTTSession {
    constructor({ onInterim, onFinal, onError, onEnd, lang }) {
        this.recognition = new SpeechRecognition();
        this.recognition.continuous = true;
        this.recognition.interimResults = true;
        this.recognition.lang = lang || detectLang();
        this.finalText = '';
        this.lastSpeechTime = Date.now();
        this.silenceTimer = null;
        this.stopped = false;

        this.recognition.onresult = (event) => {
            this.lastSpeechTime = Date.now();
            let interimText = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    this.finalText += transcript + ' ';
                } else {
                    interimText += transcript;
                }
            }
            if (onInterim) onInterim((this.finalText + interimText).trim());
            this.resetSilenceTimer();
        };

        this.recognition.onerror = (e) => {
            if (e.error === 'no-speech') return; // ignore
            if (onError) onError(e.error || 'Speech recognition failed');
        };

        this.recognition.onend = () => {
            if (this.stopped) return;
            // Auto-restart if user hasn't manually stopped (Chrome auto-stops after silence)
            try { this.recognition.start(); } catch (e) { /* ignore */ }
        };
    }

    resetSilenceTimer() {
        clearTimeout(this.silenceTimer);
        this.silenceTimer = setTimeout(() => {
            this.stop(true); // auto-stop after silence
        }, 3000); // 3 sec silence
    }

    start() {
        try {
            this.recognition.start();
            this.resetSilenceTimer();
        } catch (e) {
            if (this.onError) this.onError('Mic already active');
        }
    }

    stop(viaSilence = false) {
        this.stopped = true;
        clearTimeout(this.silenceTimer);
        try { this.recognition.stop(); } catch (e) { /* ignore */ }
        if (this._onEnd) this._onEnd({ finalText: this.finalText.trim(), viaSilence });
    }
}

export function startBrowserSTT({ onInterim, onFinal, onError, onEnd } = {}) {
    if (!hasBrowserSTT()) return null;
    const session = new BrowserSTTSession({ onInterim, onFinal, onError });
    session._onEnd = onEnd;
    session.start();
    return session;
}

/**
 * Whisper path — record audio, upload to server for transcription
 */
class WhisperSession {
    constructor({ onError, onTranscribed, onRecordingStart, onRecordingStop, maxDurationMs = 60000 }) {
        this.onError = onError;
        this.onTranscribed = onTranscribed;
        this.onRecordingStart = onRecordingStart;
        this.onRecordingStop = onRecordingStop;
        this.maxDurationMs = maxDurationMs;
        this.recorder = null;
        this.stream = null;
        this.chunks = [];
        this.maxTimer = null;
        this.stopped = false;
    }

    async start() {
        try {
            this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (e) {
            if (this.onError) this.onError('Microphone permission denied');
            return;
        }

        // Pick a supported mimeType (Safari prefers mp4, Chrome prefers webm)
        const mimeCandidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
        const supportedMime = mimeCandidates.find(m => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) || '';

        try {
            this.recorder = supportedMime
                ? new MediaRecorder(this.stream, { mimeType: supportedMime })
                : new MediaRecorder(this.stream);
        } catch (e) {
            if (this.onError) this.onError('Recording not supported');
            this._cleanup();
            return;
        }

        this.recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) this.chunks.push(e.data);
        };

        this.recorder.onstop = async () => {
            this._stopStream();
            if (this.onRecordingStop) this.onRecordingStop();
            if (this.stopped) {
                // user-cancelled — don't upload
                return;
            }
            const blob = new Blob(this.chunks, { type: supportedMime || 'audio/webm' });
            await this._uploadBlob(blob);
        };

        this.recorder.start();
        if (this.onRecordingStart) this.onRecordingStart();

        // Hard cap to avoid runaway recording
        this.maxTimer = setTimeout(() => {
            if (this.recorder && this.recorder.state === 'recording') {
                this.recorder.stop();
            }
        }, this.maxDurationMs);
    }

    async _uploadBlob(blob) {
        try {
            const formData = new FormData();
            formData.append('audio', blob, 'voice.webm');
            const lang = detectLang().startsWith('hi') ? 'hi' : 'en';
            formData.append('language', lang);
            const res = await api.post('/chat/transcribe', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const text = (res.data?.text || '').trim();
            if (this.onTranscribed) this.onTranscribed(text);
        } catch (e) {
            if (this.onError) this.onError('Transcription failed');
        }
    }

    cancel() {
        this.stopped = true;
        clearTimeout(this.maxTimer);
        if (this.recorder && this.recorder.state === 'recording') {
            this.recorder.stop();
        }
        this._stopStream();
    }

    stop() {
        clearTimeout(this.maxTimer);
        if (this.recorder && this.recorder.state === 'recording') {
            this.recorder.stop();
        }
    }

    _stopStream() {
        if (this.stream) {
            this.stream.getTracks().forEach(t => t.stop());
            this.stream = null;
        }
    }

    _cleanup() {
        this._stopStream();
        clearTimeout(this.maxTimer);
    }
}

export function startWhisperRecording(opts) {
    if (!hasMediaRecorder()) return null;
    const session = new WhisperSession(opts);
    session.start();
    return session;
}
